'use client';

import {
  specCardVariants,
  specRowVariants,
  staggerContainer,
  viewportOnce,
} from '@/features/product/motion/productMotion';
import SpecsPlaceholder from '@/features/product/specs/SpecsPlaceholder';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Button, Image } from 'react-bootstrap';
import { ChevronDoubleUp, ChevronRight } from 'react-bootstrap-icons';
import './specs.scss';

function readMessage(messages, path, fallback = '') {
  const resolved = path
    .split('.')
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

function startCase(value) {
  return String(value || '')
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatSpecValue(value, messages) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => formatSpecValue(entry, messages))
      .filter(Boolean)
      .join(', ');
  }

  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value
      ? readMessage(messages, 'specs.yes', 'Yes')
      : readMessage(messages, 'specs.no', 'No');
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    if (typeof value.value === 'string' || typeof value.value === 'number') {
      return [value.value, value.unit].filter(Boolean).join(' ');
    }

    if (Array.isArray(value.values)) {
      return value.values
        .map((entry) => formatSpecValue(entry, messages))
        .filter(Boolean)
        .join(', ');
    }
  }

  return String(value);
}

function normalizeSpecEntries(specMap, messages) {
  if (!specMap || typeof specMap !== 'object') {
    return [];
  }

  return Object.entries(specMap)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== '',
    )
    .map(([key, value]) => ({
      key,
      label:
        typeof value?.label === 'string' && value.label.trim()
          ? value.label.trim()
          : startCase(key),
      value: formatSpecValue(value, messages),
    }));
}

export default function ProductSpecsSection({ messages = {}, product }) {
  const summaryCardRef = useRef(null);
  const fullCardRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const [isExpanded, setIsExpanded] = useState(false);
  const [pendingScrollTarget, setPendingScrollTarget] = useState('');
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const baseEntries = normalizeSpecEntries(product?.baseSpecs, messages);
  const fullEntries = normalizeSpecEntries(product?.specs, messages);
  const hasBaseSummary = baseEntries.length > 0;

  const scrollToRef = (ref) => {
    if (typeof window === 'undefined' || !ref?.current) {
      return;
    }

    window.requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  useEffect(() => {
    setIsExpanded(!hasBaseSummary);
    setPendingScrollTarget('');
    setShowAllSpecs(false);
  }, [hasBaseSummary, product?.productNumber]);

  useEffect(() => {
    if (!pendingScrollTarget) {
      return;
    }

    if (pendingScrollTarget === 'full' && fullCardRef.current) {
      scrollToRef(fullCardRef);
      setPendingScrollTarget('');
      return;
    }

    if (pendingScrollTarget === 'summary' && summaryCardRef.current) {
      scrollToRef(summaryCardRef);
      setPendingScrollTarget('');
    }
  }, [pendingScrollTarget, showAllSpecs, isExpanded]);

  if (!fullEntries.length && !baseEntries.length) {
    return (
      <section className="app-section product-specs">
        <SpecsPlaceholder messages={messages} />
      </section>
    );
  }

  const summaryEntries = (baseEntries.length ? baseEntries : fullEntries).slice(
    0,
    10,
  );
  const additionalSpecs = hasBaseSummary
    ? fullEntries.filter(
        (entry) =>
          !baseEntries.some((baseEntry) => baseEntry.key === entry.key),
      )
    : [];
  const hasMoreSpecs =
    additionalSpecs.length > 0 ||
    (hasBaseSummary ? baseEntries.length > summaryEntries.length : false);
  const displayEntries = showAllSpecs ? fullEntries : fullEntries.slice(0, 15);
  const canToggleAllSpecs = fullEntries.length > 15;
  const showSummaryCard = hasBaseSummary;
  const showFullCard = !hasBaseSummary || isExpanded;
  const summaryListKey = `${product?.productNumber || 'product'}-summary-${summaryEntries.length}`;
  const fullListKey = `${product?.productNumber || 'product'}-${showAllSpecs ? 'all' : 'partial'}-${displayEntries.length}`;

  const expandSpecs = () => {
    setIsExpanded(true);
    setPendingScrollTarget('full');
  };

  const collapseSpecs = () => {
    setShowAllSpecs(false);
    setIsExpanded(false);
    setPendingScrollTarget('summary');
  };

  const toggleAllSpecs = () => {
    setShowAllSpecs((current) => !current);
    setPendingScrollTarget('full');
  };

  return (
    <section
      id="product-specs"
      className={`app-section product-specs${showFullCard ? ' product-specs--expanded' : ''}${showSummaryCard ? ' product-specs--has-summary' : ' product-specs--full-only'}`}
      ref={summaryCardRef}
    >
      {showSummaryCard ? (
        <div className="product-specs__summary-column">
          <div className="product-specs__card-header">
            <p className="product-detail-panel__eyebrow">
              {readMessage(messages, 'specs.title', 'Specifications')}
            </p>
          </div>

          <div className="product-specs__grid">
            <motion.article
              className="app-panel product-specs__summary-card product-detail-panel__summary-card"
              variants={prefersReducedMotion ? undefined : specCardVariants}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView={prefersReducedMotion ? undefined : 'visible'}
              viewport={prefersReducedMotion ? undefined : viewportOnce}
            >
              <motion.div
                key={summaryListKey}
                className="product-specs__list product-specs__list--summary"
                variants={prefersReducedMotion ? undefined : staggerContainer}
                initial={prefersReducedMotion ? false : 'hidden'}
                whileInView={prefersReducedMotion ? undefined : 'visible'}
                viewport={prefersReducedMotion ? undefined : viewportOnce}
              >
                {summaryEntries.map((entry, index) => (
                  <motion.div
                    key={entry.key}
                    className="product-specs__row"
                    variants={
                      prefersReducedMotion ? undefined : specRowVariants
                    }
                    custom={index}
                  >
                    <div className="product-specs__key">{entry.label}</div>
                    <div className="product-specs__value">{entry.value}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.article>

            {hasMoreSpecs && !showFullCard ? (
              <aside className="text-end product-specs__summary-actions">
                <Button
                  variant="link"
                  className="custom-btn-link flush product-specs__toggle-link"
                  onClick={expandSpecs}
                >
                  <span>
                    {readMessage(messages, 'specs.seeMore', 'See More')}
                  </span>
                  <ChevronRight size={14} aria-hidden="true" />
                </Button>
              </aside>
            ) : null}
          </div>
        </div>
      ) : null}

      {showFullCard ? (
        <div
          id="product-specs__full-row"
          className="product-specs__full-row"
          ref={fullCardRef}
        >
          <div className="product-specs__card-header">
            <p className="product-detail-panel__eyebrow">
              {readMessage(messages, 'specs.title-all', 'All Specifications')}
            </p>
          </div>

          <div
            className={`product-specs__grid${showFullCard && !showSummaryCard ? ' product-specs__grid--full' : ''}`}
          >
            <motion.article
              className="app-panel product-specs__full-card product-detail-panel__summary-card"
              variants={prefersReducedMotion ? undefined : specCardVariants}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView={prefersReducedMotion ? undefined : 'visible'}
              viewport={prefersReducedMotion ? undefined : viewportOnce}
            >
              <motion.div
                key={fullListKey}
                className="product-specs__list product-specs__list--full"
                variants={prefersReducedMotion ? undefined : staggerContainer}
                initial={prefersReducedMotion ? false : 'hidden'}
                animate={prefersReducedMotion ? undefined : 'visible'}
              >
                {displayEntries.map((entry, index) => (
                  <motion.div
                    key={entry.key}
                    className="product-specs__row"
                    variants={
                      prefersReducedMotion ? undefined : specRowVariants
                    }
                    custom={index}
                  >
                    <div className="product-specs__key">{entry.label}</div>
                    <div className="product-specs__value">{entry.value}</div>
                  </motion.div>
                ))}
              </motion.div>

              {canToggleAllSpecs ? (
                <Button
                  variant="link"
                  className="custom-btn-link flush text-info product-specs__toggle-link product-specs__toggle-link--all"
                  onClick={toggleAllSpecs}
                >
                  {showAllSpecs
                    ? readMessage(messages, 'specs.seeLessSpecs', 'See Less')
                    : readMessage(messages, 'specs.seeAllSpecs', 'See All')}
                  <Image
                    src="/icons/select-all.png"
                    alt=""
                    width={20}
                    height={20}
                  />{' '}
                </Button>
              ) : null}
            </motion.article>

            <aside className="text-center product-specs__full-actions">
              {hasBaseSummary ? (
                <Button
                  variant="link"
                  className="custom-btn-link flush product-specs__toggle-link"
                  onClick={collapseSpecs}
                  title={readMessage(
                    messages,
                    'specs.foldTitle',
                    'Fold specifications',
                  )}
                >
                  <ChevronDoubleUp size={24} aria-hidden="true" />
                  <span className="visually-hidden">
                    {readMessage(
                      messages,
                      'specs.foldTitle',
                      'Fold specifications',
                    )}
                  </span>
                </Button>
              ) : null}
            </aside>
          </div>
        </div>
      ) : null}
    </section>
  );
}
